import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  DollarSign, 
  TrendingUp, 
  Calendar,
  AlertTriangle,
  Clock,
  Filter,
  Download,
  RefreshCw,
  Percent,
  Car,
  User
} from 'lucide-react';
import { apiService } from '../../services/api';

const GordinhaPage = () => {
  const [registros, setRegistros] = useState([]);
  const [resumoMensal, setResumoMensal] = useState([]);
  const [totais, setTotais] = useState({});
  const [loading, setLoading] = useState(true);
  const [mesSelecionado, setMesSelecionado] = useState('');
  const [abaAtiva, setAbaAtiva] = useState('extrato');

  useEffect(() => {
    carregarDados();
  }, [mesSelecionado]);

  const carregarDados = async () => {
    setLoading(true);
    try {
      const [extrato, resumo] = await Promise.all([
        apiService.api.get(`/gordurinha${mesSelecionado ? `?mes=${mesSelecionado}` : ''}`),
        apiService.api.get('/gordurinha/resumo-mensal')
      ]);
      
      setRegistros(extrato.data.registros || []);
      setTotais(extrato.data.totais || {});
      setResumoMensal(resumo.data.resumo_mensal || []);
    } catch (error) {
      console.error('Erro ao carregar gordurinha:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-BR');
  };

  const formatMes = (mesStr) => {
    if (!mesStr) return '-';
    const [ano, mes] = mesStr.split('-');
    const meses = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
    return `${meses[parseInt(mes) - 1]}/${ano}`;
  };

  const getTipoBadge = (tipo) => {
    switch (tipo) {
      case 'multa_transito':
        return <Badge className="bg-purple-100 text-purple-300">20% Multa</Badge>;
      case 'juros_atraso':
        return <Badge className="bg-orange-100 text-orange-700">Juros Atraso</Badge>;
      case 'multa_atraso':
        return <Badge className="bg-red-100 text-rose-300">Multa Atraso</Badge>;
      default:
        return <Badge variant="secondary">{tipo}</Badge>;
    }
  };

  // Gerar lista de meses para filtro
  const mesesDisponiveis = [...new Set(registros.map(r => r.mes_referencia))].sort().reverse();

  return (
    <div className="p-4 lg:p-6 space-y-6" data-testid="gordurinha-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <TrendingUp className="h-6 w-6 text-emerald-400" />
            Gordurinha
          </h1>
          <p className="text-sm text-gray-300 mt-1">
            Ganhos extras com multas (20%) e juros por atraso
          </p>
        </div>
        
        <Button onClick={carregarDados} variant="outline" size="sm">
          <RefreshCw className="h-4 w-4 mr-2" />
          Atualizar
        </Button>
      </div>

      {/* Cards de Totais */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-purple-600 to-violet-700 border-purple-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Percent className="h-4 w-4 text-purple-400" />
              <span className="text-xs font-medium text-purple-400">20% Multas</span>
            </div>
            <p className="text-xl font-bold text-purple-300">
              {formatCurrency(totais.multas_transito_20)}
            </p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-orange-500 to-amber-600 border-orange-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="h-4 w-4 text-orange-600" />
              <span className="text-xs font-medium text-orange-600">Juros Atraso</span>
            </div>
            <p className="text-xl font-bold text-orange-700">
              {formatCurrency(totais.juros_atraso)}
            </p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-rose-600 to-red-700 border-red-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="h-4 w-4 text-rose-400" />
              <span className="text-xs font-medium text-rose-400">Multa Atraso</span>
            </div>
            <p className="text-xl font-bold text-rose-300">
              {formatCurrency(totais.multa_atraso)}
            </p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-emerald-600 to-teal-700 border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <DollarSign className="h-4 w-4 text-emerald-400" />
              <span className="text-xs font-medium text-emerald-400">Total Geral</span>
            </div>
            <p className="text-2xl font-bold text-emerald-300">
              {formatCurrency(totais.total_geral)}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Abas */}
      <div className="flex gap-2 border-b">
        <button
          onClick={() => setAbaAtiva('extrato')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            abaAtiva === 'extrato' 
              ? 'border-green-600 text-emerald-400' 
              : 'border-transparent text-gray-400 hover:text-gray-200'
          }`}
        >
          Extrato Detalhado
        </button>
        <button
          onClick={() => setAbaAtiva('mensal')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            abaAtiva === 'mensal' 
              ? 'border-green-600 text-emerald-400' 
              : 'border-transparent text-gray-400 hover:text-gray-200'
          }`}
        >
          Resumo Mensal
        </button>
      </div>

      {/* Filtro por Mês */}
      {abaAtiva === 'extrato' && (
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-gray-400" />
          <select
            value={mesSelecionado}
            onChange={(e) => setMesSelecionado(e.target.value)}
            className="text-sm border rounded-md px-3 py-1.5"
          >
            <option value="">Todos os meses</option>
            {mesesDisponiveis.map(mes => (
              <option key={mes} value={mes}>{formatMes(mes)}</option>
            ))}
          </select>
        </div>
      )}

      {/* Conteúdo */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      ) : abaAtiva === 'extrato' ? (
        /* Extrato Detalhado */
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">
              Extrato {mesSelecionado ? `- ${formatMes(mesSelecionado)}` : ''}
              <span className="text-sm font-normal text-gray-400 ml-2">
                ({registros.length} registros)
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {registros.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <TrendingUp className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>Nenhum registro de gordurinha ainda</p>
                <p className="text-sm mt-1">Os valores serão registrados automaticamente quando pagamentos com juros/multa forem confirmados</p>
              </div>
            ) : (
              <div className="space-y-3">
                {registros.map((registro) => (
                  <div
                    key={registro.id}
                    className="flex flex-col lg:flex-row lg:items-center justify-between p-3 bg-slate-800/50 rounded-lg border hover:bg-slate-700/50 transition-colors"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        {getTipoBadge(registro.tipo)}
                        <span className="text-xs text-gray-400">
                          {formatDate(registro.data_registro)}
                        </span>
                      </div>
                      <p className="text-sm font-medium text-white">
                        {registro.descricao}
                      </p>
                      <div className="flex items-center gap-4 mt-1 text-xs text-gray-400">
                        <span className="flex items-center gap-1">
                          <User className="h-3 w-3" />
                          {registro.cliente_nome}
                        </span>
                        <span className="flex items-center gap-1">
                          <Car className="h-3 w-3" />
                          {registro.moto_placa}
                        </span>
                      </div>
                    </div>
                    
                    <div className="text-right mt-2 lg:mt-0">
                      {registro.tipo === 'multa_transito' && (
                        <p className="text-xs text-gray-400">
                          {registro.percentual}% de {formatCurrency(registro.valor_original)}
                        </p>
                      )}
                      <p className="text-lg font-bold text-emerald-400">
                        +{formatCurrency(registro.valor_gordura)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        /* Resumo Mensal */
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Resumo por Mês</CardTitle>
          </CardHeader>
          <CardContent>
            {resumoMensal.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <Calendar className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>Nenhum registro ainda</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-2">Mês</th>
                      <th className="text-right py-3 px-2 text-purple-400">20% Multas</th>
                      <th className="text-right py-3 px-2 text-orange-600">Juros</th>
                      <th className="text-right py-3 px-2 text-rose-400">Multa Atraso</th>
                      <th className="text-right py-3 px-2 text-emerald-400 font-bold">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {resumoMensal.map((mes) => (
                      <tr key={mes.mes} className="border-b hover:bg-slate-800/50">
                        <td className="py-3 px-2 font-medium">{formatMes(mes.mes)}</td>
                        <td className="text-right py-3 px-2 text-purple-400">
                          {formatCurrency(mes.multas_transito_20)}
                        </td>
                        <td className="text-right py-3 px-2 text-orange-600">
                          {formatCurrency(mes.juros_atraso)}
                        </td>
                        <td className="text-right py-3 px-2 text-rose-400">
                          {formatCurrency(mes.multa_atraso)}
                        </td>
                        <td className="text-right py-3 px-2 text-emerald-400 font-bold">
                          {formatCurrency(mes.total)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-green-50">
                      <td className="py-3 px-2 font-bold text-green-800">TOTAL GERAL</td>
                      <td className="text-right py-3 px-2 font-bold text-purple-300">
                        {formatCurrency(resumoMensal.reduce((acc, m) => acc + m.multas_transito_20, 0))}
                      </td>
                      <td className="text-right py-3 px-2 font-bold text-orange-700">
                        {formatCurrency(resumoMensal.reduce((acc, m) => acc + m.juros_atraso, 0))}
                      </td>
                      <td className="text-right py-3 px-2 font-bold text-rose-300">
                        {formatCurrency(resumoMensal.reduce((acc, m) => acc + m.multa_atraso, 0))}
                      </td>
                      <td className="text-right py-3 px-2 font-bold text-green-800 text-lg">
                        {formatCurrency(resumoMensal.reduce((acc, m) => acc + m.total, 0))}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default GordinhaPage;
